<template>
  <!-- Backdrop overlay for mobile -->
  <div 
    v-if="isMobileOpen"
    class="sidebar-overlay fixed inset-0 bg-black/40 z-40 md:hidden"
    @click="closeMobile"
  />

  <div
    :class="[
      isExpanded ? 'sidebar-expanded' : 'sidebar-collapsed',
      'sidebar',
      isMobileOpen ? 'mobile-open' : ''
    ]" style="background: white;"
  >
    <!-- Toggle Button (desktop) -->
    <div 
      class="toggle-button text-right p-2"
      @click="toggleSidebar"
    >
      <i class="material-icons-outlined">menu</i>
    </div>

    <!-- Close Button (mobile only) -->
    <button
      class="absolute right-3 top-3 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center md:hidden z-10 transition-colors"
      @click="closeMobile"
    >
      <i class="material-icons-outlined text-gray-500" style="font-size:20px;">close</i>
    </button>

    <!-- Menu Items -->
    <div class="px-2 py-4 space-y-1">
      <template v-for="item in filteredMenuItems" :key="item.id">
        <template v-if="canUserAccess(item.roles)">
          <!-- Upcoming Module (Próximamente) -->
          <div v-if="item.isUpcoming" class="relative">
            <button 
              type="button"
              class="menu-link w-full text-left group hover:bg-amber-50/70 transition-colors"
              :title="`${item.name} - Próximamente`"
              @click="handleUpcomingClick(item)"
            >
              <div class="menu-icon relative">
                <i 
                  class="material-icons-outlined mr-2 text-gray-400 group-hover:text-amber-500 transition-colors"
                >{{ item.icon }}</i>
                <span 
                  v-if="!isExpanded"
                  class="absolute -top-0.5 right-0.5 w-2 h-2 rounded-full bg-amber-500 ring-2 ring-white"
                  title="Próximamente"
                />
              </div>

              <div 
                :class="[
                  'menu-text ml-3 flex items-center justify-between flex-1 overflow-hidden',
                  {'menu-text-hidden': !isExpanded}
                ]"
              >
                <span class="text-gray-600 group-hover:text-gray-900 font-normal truncate mr-2">{{ item.name }}</span>
                <span class="text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200/80 shrink-0">
                  Próx.
                </span>
              </div>
            </button>
          </div>

          <!-- Active Link -->
          <div class="relative">
            <Link 
              class="menu-link" 
              :class="{ 
                'active': isMenuItemActive(item),
                'opacity-80 hover:opacity-100': !canAccessFeature(item.id)
              }"
              :href="canAccessFeature(item.id) ? item.route : '/subscription/plans'"
              :title="isPremiumFeature(item.id) && isFreePlan ? `${item.name} (Función PRO)` : item.name"
              @mouseenter="hoveredItem = item.id"
              @mouseleave="hoveredItem = null"
              @click="handleMenuItemClick"
            >
              <div class="menu-icon relative">
                <!-- Bloqueo Icon Overlay si no tiene acceso -->
                <div v-if="!canAccessFeature(item.id)" class="absolute -top-1 -right-1 z-20">
                  <i class="material-icons-outlined text-[11px] text-orange-500 bg-white rounded-full p-0.5 shadow-sm">lock</i>
                </div>
                <i 
                  class="material-icons-outlined mr-2"
                  :class="[
                    isMenuItemActive(item) ? 'text-white' : 'text-gray-400 group-hover:text-white menu-item'
                  ]" 
                  :style="{'--text-color': 'black', '--hover-color': 'orange'}"
                >{{ item.icon }}</i>
              </div>

              <div 
                :class="[
                  'menu-text ml-3 flex-1 flex items-center justify-between min-w-0 pr-1',
                  {'menu-text-hidden': !isExpanded},
                  isMenuItemActive(item) ? 'menu-item font-bold' : 'text-gray-400 group-hover:text-white menu-item'
                ]" 
                :style="{'--text-color': 'black', '--hover-color': 'black'}"
              >
                <span class="truncate mr-2">{{ item.name }}</span>
                <!-- Badge de PRO para incitar a la compra -->
                <span
                  v-if="isPremiumFeature(item.id) && isFreePlan" 
                  class="bg-gradient-to-r from-orange-500 to-amber-500 text-white text-[9px] font-black uppercase px-2 py-0.5 rounded-full shadow-sm tracking-wider shrink-0"
                >
                  PRO
                </span>
              </div>
            </Link>
          </div>
        </template>
      </template>

      <!-- Botón Conviértete en PRO (Llamativo) -->
      <div v-if="isFreePlan || !isSubscriptionActive" class="mt-8 px-2">
        <Link href="/subscription/plans" class="block group w-full rounded-2xl border-2 border-orange-500 hover:bg-orange-500 transition-all duration-300 hover:shadow-orange-500/30 bg-transparent">
          <div class="relative flex items-center justify-center px-4 py-3">
            <div :class="{'hidden': !isExpanded}" class="font-black text-orange-500 group-hover:text-white text-[11px] uppercase tracking-[0.2em] whitespace-nowrap flex items-center gap-2 transition-colors duration-300">
              <i class="material-icons-outlined text-orange-500 group-hover:text-white text-[16px] transition-colors duration-300">stars</i>
              {{ isFreePlan ? 'Pásate a PRO' : 'Renovar Ahora' }}
            </div>
            <i v-if="!isExpanded" class="material-icons-outlined text-orange-500 group-hover:text-white transition-colors duration-300">workspace_premium</i>
          </div>
        </Link>
      </div>
    </div>
  </div>
</template>

<script>
import { Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'

export default {
  components: {
    Icon,
    Link,
  },
  props: {
    account: Object,
  },
  data() {
    return {
      role: this.$page?.props?.auth?.user?.role || 'guest',
      isExpanded: true,
      isMobileOpen: false,
      hoveredItem: null,
      freePlanFeatures: [
        'dashboard', 'clients', 'products', 'inventory', 'orders', 'pos',
        'reports', 'catalog', 'config', 'tickets', 'admin-tickets', 'quotes', 
        'shopping', 'spents', 'shipments', 'caja',
        'subscriptions_module', 'payments_module', 'paysheet',
      ],
    }
  },
  computed: {
    isFreePlan() {
      return this.$page?.props?.auth?.user?.account?.plan === 'free'
    },
    isSubscriptionActive() {
      const account = this.$page?.props?.auth?.user?.account
      if (!account) return false
      if (account.plan === 'free') return false
      
      // 1. Intentar por la fecha si existe y es válida
      if (account.plan_expires_at) {
        const expiresAt = new Date(account.plan_expires_at)
        if (!isNaN(expiresAt.getTime())) {
          return expiresAt > new Date()
        }
      }
      
      // 2. Fallback al estado del servidor (ahora disponible en el objeto account)
      return !!account.has_active_subscription
    },
    currentAccount() {
      return this.account || this.$page?.props?.auth?.user?.account || {}
    },
    accountFlavor() {
      const acc = this.currentAccount
      const rawType = (acc.business_type || '').toLowerCase().trim()
      
      if (rawType === 'wholesale' || rawType === 'mayorista') {
        return 'wholesale'
      }
      if (rawType === 'restaurant' || rawType === 'food' || acc.restaurant_mode) {
        return 'restaurant'
      }
      if (rawType === 'services' || rawType === 'servicios') {
        return 'services'
      }
      if (rawType === 'subscriptions' || rawType === 'suscripciones') {
        return 'subscriptions'
      }
      return 'retail'
    },
    filteredMenuItems() {
      const accountId = this.currentAccount?.id ?? ''
      // Excluir módulos "Próximamente" para mantener el menú limpio y 100% operativo
      return this.getFlavorItems(this.accountFlavor, accountId).filter(item => !item.isUpcoming)
    },
  },
  created() {
    // Solo para desktop, leer el estado del localStorage
    const isMobile = window.innerWidth <= 768
    if (!isMobile) {
      const stored = localStorage.getItem('sidebarExpanded')
      this.isExpanded = stored === 'true'
    }
  },
  mounted() {
    // Desktop: leer estado guardado
    if (window.innerWidth > 768) {
      const stored = localStorage.getItem('sidebarExpanded')
      this.isExpanded = stored !== null ? stored === 'true' : true
    } else {
      // Mobile: siempre cerrado al iniciar
      this.isMobileOpen = false
      this.isExpanded = true // El menú está expandido cuando se abre
    }
  },
  methods: {
    handleMenuItemClick() {
      // En mobile, cerrar el drawer al navegar
      if (window.innerWidth <= 768) {
        this.isMobileOpen = false
      }
    },
    handleUpcomingClick(item) {
      if (this.$swal) {
        this.$swal.fire({
          title: `¡Módulo de ${item.name}!`,
          text: `El módulo de ${item.name} estará disponible próximamente para tu tipo de negocio.`,
          icon: 'info',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#f97316',
        })
      } else if (this.$toast) {
        this.$toast.info(`El módulo de ${item.name} estará disponible próximamente`)
      } else {
        alert(`El módulo de ${item.name} estará disponible próximamente`)
      }
    },
    closeMobile() {
      this.isMobileOpen = false
    },
    openMobile() {
      this.isMobileOpen = true
    },
    back() {
      this.$inertia.visit('/')
    },
    isUrl(...urls) {
      let currentUrl = this.$page.url.replace(/^\//, '')
      if (urls[0] === '') {
        return currentUrl === '' || currentUrl.startsWith('dashboard')
      }
      return urls.some(url => currentUrl.startsWith(url))
    },
    isMenuItemActive(item) {
      if (item.isUpcoming || !item.route) return false
      if (item.id === 'shopping' && (this.isUrl('shoppings') || this.isUrl('spents') || this.isUrl('spends'))) {
        return true
      }
      const cleanRoute = item.route.replace(/^\//, '').split('?')[0].split('/')[0]
      if (cleanRoute === 'dashboard') {
        const path = this.$page.url.replace(/^\//, '').split('?')[0]
        return path === '' || path === 'dashboard'
      }
      return this.isUrl(cleanRoute)
    },
    toggleSidebar() {
      if (window.innerWidth <= 768) {
        // En mobile, toggle del drawer
        this.isMobileOpen = !this.isMobileOpen
      } else {
        // En desktop, toggle del collapse
        this.isExpanded = !this.isExpanded
        localStorage.setItem('sidebarExpanded', this.isExpanded)
      }
    },
    canUserAccess(roles) {
      if (!roles || roles.includes('*') || this.role === 'superadmin') return true
      return roles.includes(this.role)
    },
    isPremiumFeature(featureId) {
      return !this.freePlanFeatures.includes(featureId)
    },
    canAccessFeature(featureId) {
      // 1. Si la característica es del plan gratuito, siempre accesible
      if (this.freePlanFeatures.includes(featureId)) return true
      
      // 2. Si es premium, requiere suscripción activa
      return this.isSubscriptionActive
    },
    getFlavorItems(flavor, accountId) {
      const configRoute = `/account/${accountId}?mode=informacion-general`
      
      const flavorDefinitions = {
        // 1. Minorista o detal
        retail: [
          { id: 'clients', name: 'Clientes', icon: 'contact_phone', route: '/clients', roles: ['admin', 'sales', 'financial'] },
          { id: 'products', name: 'Productos', icon: 'inventory_2', route: '/products', roles: ['admin', 'endline', 'storer', 'sales'] },
          { id: 'orders', name: 'Ventas', icon: 'receipt_long', route: '/orders', roles: ['sales', 'admin', 'storer', 'financial'] },
          { id: 'catalog', name: 'Catálogo digital', icon: 'storefront', route: '/my-catalog', roles: ['admin', 'sales', 'financial'] },
          { id: 'shopping', name: 'Compras y Gastos', icon: 'shopping_bag', route: '/shoppings', roles: ['admin', 'sales', 'financial'] },
          { id: 'paysheet', name: 'Nómina / Turnos', icon: 'badge', route: '/paysheet', roles: ['admin', 'financial'] },
          { id: 'caja', name: 'Caja', icon: 'account_balance_wallet', route: '#', isUpcoming: true, roles: ['admin', 'sales', 'financial'] },
          { id: 'inventory', name: 'Inventario', icon: 'warehouse', route: '/inventories', roles: ['admin', 'endline', 'storer'] },
          { id: 'reports', name: 'Reportes', icon: 'assessment', route: '/reports', roles: ['sales', 'admin'] },
          { id: 'config', name: 'Configuración', icon: 'settings', route: configRoute, roles: ['admin'] },
        ],

        // 2. Mayorista o distribuidora
        wholesale: [
          { id: 'clients', name: 'Clientes', icon: 'contact_phone', route: '/clients', roles: ['admin', 'sales', 'financial'] },
          { id: 'products', name: 'Productos', icon: 'inventory_2', route: '/products', roles: ['admin', 'endline', 'storer', 'sales'] },
          { id: 'orders', name: 'Ventas', icon: 'receipt_long', route: '/orders', roles: ['sales', 'admin', 'storer', 'financial'] },
          { id: 'catalog', name: 'Catálogo digital', icon: 'storefront', route: '/my-catalog', roles: ['admin', 'sales', 'financial'] },
          { id: 'quotes', name: 'Cotización', icon: 'request_quote', route: '/quotes/create', roles: ['admin', 'sales', 'financial'] },
          { id: 'pos', name: 'POS', icon: 'point_of_sale', route: '/pos', roles: ['sales', 'admin', 'storer', 'financial'] },
          { id: 'shopping', name: 'Compras y Gastos', icon: 'shopping_bag', route: '/shoppings', roles: ['admin', 'sales', 'financial'] },
          { id: 'paysheet', name: 'Nómina / Turnos', icon: 'badge', route: '/paysheet', roles: ['admin', 'financial'] },
          { id: 'caja', name: 'Caja', icon: 'account_balance_wallet', route: '#', isUpcoming: true, roles: ['admin', 'sales', 'financial'] },
          { id: 'shipments', name: 'Despachos', icon: 'local_shipping', route: '/shipments', roles: ['admin', 'storer', 'sales'] },
          { id: 'inventory', name: 'Inventario', icon: 'warehouse', route: '/inventories', roles: ['admin', 'endline', 'storer'] },
          { id: 'reports', name: 'Reportes', icon: 'assessment', route: '/reports', roles: ['sales', 'admin'] },
          { id: 'cobranzas', name: 'Cobranzas', icon: 'payments', route: '/cobranzas', roles: ['admin', 'financial'] },
          { id: 'config', name: 'Configuración', icon: 'settings', route: configRoute, roles: ['admin'] },
        ],

        // 3. Restaurantes / Bares
        restaurant: [
          { id: 'clients', name: 'Clientes', icon: 'contact_phone', route: '/clients', roles: ['admin', 'sales', 'financial'] },
          { id: 'products', name: 'Productos', icon: 'inventory_2', route: '/products', roles: ['admin', 'endline', 'storer', 'sales'] },
          { id: 'orders', name: 'Ventas', icon: 'receipt_long', route: '/orders', roles: ['sales', 'admin', 'storer', 'financial'] },
          { id: 'shopping', name: 'Compras y Gastos', icon: 'shopping_bag', route: '/shoppings', roles: ['admin', 'sales', 'financial'] },
          { id: 'paysheet', name: 'Nómina / Turnos', icon: 'badge', route: '/paysheet', roles: ['admin', 'financial'] },
          { id: 'pos', name: 'POS (Restaurante)', icon: 'restaurant', route: '/pos', roles: ['sales', 'admin', 'storer', 'financial'] },
          { id: 'boards', name: 'Comandas', icon: 'view_kanban', route: '/boards', roles: ['sales', 'admin', 'storer', 'financial'] },
          { id: 'catalog', name: 'Catálogo digital', icon: 'storefront', route: '/my-catalog', roles: ['admin', 'sales', 'financial'] },
          { id: 'mesas', name: 'Mesas', icon: 'table_restaurant', route: '#', isUpcoming: true, roles: ['admin', 'sales', 'storer'] },
          { id: 'caja', name: 'Caja', icon: 'account_balance_wallet', route: '#', isUpcoming: true, roles: ['admin', 'sales', 'financial'] },
          { id: 'inventory', name: 'Inventario', icon: 'warehouse', route: '/inventories', roles: ['admin', 'endline', 'storer'] },
          { id: 'reports', name: 'Reportes', icon: 'assessment', route: '/reports', roles: ['sales', 'admin'] },
          { id: 'config', name: 'Configuración', icon: 'settings', route: configRoute, roles: ['admin'] },
        ],

        // 4. Venta de servicios
        services: [
          { id: 'clients', name: 'Clientes', icon: 'contact_phone', route: '/clients', roles: ['admin', 'sales', 'financial'] },
          { id: 'cobranzas', name: 'Cobranza', icon: 'payments', route: '/cobranzas', roles: ['admin', 'financial'] },
          { id: 'products', name: 'Productos / Servicios', icon: 'miscellaneous_services', route: '/products', roles: ['admin', 'endline', 'storer', 'sales'] },
          { id: 'orders', name: 'Ventas', icon: 'receipt_long', route: '/orders', roles: ['sales', 'admin', 'storer', 'financial'] },
          { id: 'catalog', name: 'Catálogo digital', icon: 'storefront', route: '/my-catalog', roles: ['admin', 'sales', 'financial'] },
          { id: 'shopping', name: 'Compras y Gastos', icon: 'shopping_bag', route: '/shoppings', roles: ['admin', 'sales', 'financial'] },
          { id: 'paysheet', name: 'Nómina / Turnos', icon: 'badge', route: '/paysheet', roles: ['admin', 'financial'] },
          { id: 'quotes', name: 'Cotización', icon: 'request_quote', route: '/quotes/create', roles: ['admin', 'sales', 'financial'] },
          { id: 'reports', name: 'Reportes', icon: 'assessment', route: '/reports', roles: ['sales', 'admin'] },
          { id: 'config', name: 'Configuración', icon: 'settings', route: configRoute, roles: ['admin'] },
        ],

        // 5. Suscripciones / Membresias
        subscriptions: [
          { id: 'clients', name: 'Clientes', icon: 'contact_phone', route: '/clients', roles: ['admin', 'sales', 'financial'] },
          { id: 'products', name: 'Productos', icon: 'inventory_2', route: '/products', roles: ['admin', 'endline', 'storer', 'sales'] },
          { id: 'orders', name: 'Ventas', icon: 'receipt_long', route: '/orders', roles: ['sales', 'admin', 'storer', 'financial'] },
          { id: 'catalog', name: 'Catálogo digital', icon: 'storefront', route: '/my-catalog', roles: ['admin', 'sales', 'financial'] },
          { id: 'shopping', name: 'Compras y Gastos', icon: 'shopping_bag', route: '/shoppings', roles: ['admin', 'sales', 'financial'] },
          { id: 'paysheet', name: 'Nómina / Turnos', icon: 'badge', route: '/paysheet', roles: ['admin', 'financial'] },
          { id: 'subscriptions_module', name: 'Suscripciones', icon: 'card_membership', route: '#', isUpcoming: true, roles: ['admin', 'sales', 'financial'] },
          { id: 'payments_module', name: 'Pagos', icon: 'paid', route: '#', isUpcoming: true, roles: ['admin', 'sales', 'financial'] },
          { id: 'reports', name: 'Reportes', icon: 'assessment', route: '/reports', roles: ['sales', 'admin'] },
          { id: 'config', name: 'Configuración', icon: 'settings', route: configRoute, roles: ['admin'] },
        ],
      }

      const items = flavorDefinitions[flavor] || flavorDefinitions.retail
      const menuItems = [...items]

      // Asegurar que Catálogo digital siempre exista en el sidebar
      if (!menuItems.some(item => item.id === 'catalog')) {
        const insertIndex = menuItems.findIndex(item => item.id === 'reports' || item.id === 'config')
        const catalogItem = { id: 'catalog', name: 'Catálogo digital', icon: 'storefront', route: '/my-catalog', roles: ['admin', 'sales', 'financial'] }
        if (insertIndex !== -1) {
          menuItems.splice(insertIndex, 0, catalogItem)
        } else {
          menuItems.push(catalogItem)
        }
      }

      return [
        { id: 'dashboard', name: 'Escritorio', icon: 'home', route: '/dashboard', roles: ['*'] },
        ...menuItems,
        { id: 'tickets', name: 'Soporte', icon: 'help_outline', route: '/tickets', roles: ['admin', 'sales', 'financial', 'endline', 'storer', 'dealer', 'supersales'] },
        { id: 'admin-tickets', name: 'Soporte', icon: 'support_agent', route: '/admin/tickets', roles: ['superadmin'] },
      ]
    },
  },
}
</script>

<style>
.menu-item {
  padding: 0;
  color: black !important;
}
.menu-item:hover {
  color: black !important;
}

.sidebar {
  transition: all 0.3s ease;
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
}

.sidebar::-webkit-scrollbar {
  width: 4px;
}
.sidebar::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.15);
  border-radius: 4px;
}

.sidebar-expanded {
  width: 224px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 2.5rem 0.75rem 2rem 0.75rem;
}

.sidebar-collapsed {
  width: 80px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 2.5rem 0.5rem 2rem 0.5rem;
}

.menu-text {
  transition: all 0.3s ease;
  white-space: nowrap;
}

.menu-text-hidden {
  opacity: 0;
  width: 0;
  overflow: hidden;
}

.menu-icon {
  min-width: 2rem;
  width: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.2rem;
}

.menu-link {
  padding: 0.75rem;
  border-radius: 0.5rem;
  margin: 0.25rem 0;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  text-align: center;
}

.menu-link:hover {
  color: black !important;
  width: 100%;
  text-align: left;
  justify-content: flex-start;
  align-items: flex-start;
  transition: all 0.2s ease;
}

.menu-link.active {
  color: orange !important;
  width: 100%;
  text-align: left;
  justify-content: flex-start;
  align-items: flex-start;
  transition: all 0.2s ease;
  background-color: rgba(255, 203, 124, 0.787) !important;
}

.toggle-button {
  position: absolute;
  right: 1rem;
  top: 1rem;
  width: 2rem;
  height: 2rem;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
  z-index: 10;
  transition: all 0.3s ease;
}

@media (max-width: 768px) {
  .sidebar {
    position: fixed !important;
    top: 0;
    left: 0;
    height: 100vh;
    z-index: 50;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    width: 260px !important;
    box-shadow: 4px 0 24px rgba(0,0,0,0.12);
  }

  .sidebar.mobile-open {
    transform: translateX(0);
  }

  .sidebar-overlay {
    display: block;
  }

  .toggle-button {
    display: none;
  }

  .menu-text {
    opacity: 1 !important;
    width: auto !important;
  }
}

@media (min-width: 769px) {
  .sidebar-overlay {
    display: none !important;
  }
}

.toggle-button:hover {
  background: #f8f8f8;
  transform: scale(1.1);
}

.toggle-button i {
  color: #333;
  font-size: 0.8rem;
  transition: all 0.3s ease;
}

@keyframes rainbow {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

.hover-rainbow:hover {
  background: linear-gradient(270deg, #ff962d, #fc962f, #f98912, #f6720e, #f35b0b, #f03e08);
  background-size: 400% 400%;
  animation: rainbow 3s ease infinite;
}
</style>
